<template>
  <div>
    <q-table
      flat
      :columns="columns"
      :rows="rows"
      :pagination="{
        rowsPerPage: 20
      }"
      dense
      :bordered="bordered"
      :row-key="getId"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
      :hide-pagination="printingMode"
    >
      <template #header-cell="props">
        <q-th :props="props">
          <q-btn
            v-if="columnFilterMap[props.col.name]"
            flat
            dense
            size="xs"
            :color="isColumnFilterActive(props.col.name) ? 'primary' : 'grey-6'"
            class="q-mr-xs"
            @click.stop
          >
            <q-icon name="filter_list" />
            <q-menu
              :auto-close="false"
              transition-duration="100"
              style="width: 280px"
              @before-show="initPendingFilter(props.col.name)"
              @hide="onMenuHide(props.col.name)"
            >
              <filter-popover
                v-if="pendingSelectedFilter"
                :selected-filter="pendingSelectedFilter"
                @update:values="pendingFilter.values = $event"
                @update:min-value="pendingFilter.minValue = $event"
                @update:max-value="pendingFilter.maxValue = $event"
              />
              <q-separator />
              <q-card-actions align="right">
                <q-btn
                  v-show="isColumnFilterActive(props.col.name)"
                  v-close-popup
                  flat
                  no-caps
                  dense
                  color="negative"
                  label="Clear"
                  @click="emitRemoveFilter(props.col.name)"
                />
                <q-space />
                <q-btn v-close-popup flat no-caps dense label="Cancel" />
                <q-btn
                  v-close-popup
                  color="primary"
                  dense
                  label="Apply"
                  no-caps
                  unelevated
                  @click="emitApplyFilter(props.col.name)"
                />
              </q-card-actions>
            </q-menu>
          </q-btn>
          <span @click="sortingChanged(props.col.name)" class="q-pr-xs">
            {{ props.col.label }}
          </span>
          <q-tooltip v-if="props.col.description" max-width="250px">
            <b>{{ props.col.label }}</b>
            <br />
            {{ props.col.description }}
          </q-tooltip>
        </q-th>
      </template>
      <template #body="props">
        <q-tr :props="props">
          <q-td
            v-for="col in props.cols"
            :id="`intercom-${col.name}-${props.rowIndex}`"
            :key="col.name"
            :props="props"
          >
            <div v-if="col.name === 'bed'" :class="`hd-${props.row.unit.bed}-bed-bg hd-badge`">
              {{ col.value }}
            </div>
            <template v-else-if="col.name === 'unit_name'">
              <a
                v-if="expandedUnitHistory[props.row.unit.id]?.length"
                href="#"
                class="text-primary"
                @click="openPriceHistory(props.row)"
              >
                {{ col.value || 'N/A' }}
              </a>
              <span v-else>{{ col.value || 'N/A' }}</span>
              <q-btn
                v-if="
                  (props.row.unit.amenities?.length || props.row.unit.tags?.length) && col.value
                "
                color="primary"
                class="q-ml-xs"
                flat
                clickable
                dense
                size="xs"
              >
                <div>
                  <q-icon name="more" size="small" />
                  <q-icon name="arrow_drop_down" size="small" />
                  <q-tooltip>
                    {{ props.row.unit.amenities?.length }} amenities
                    <template v-if="props.row.unit.tags?.length">
                      + {{ props.row.unit.tags?.length }} AI-Detected amenities
                    </template>
                  </q-tooltip>
                </div>
                <q-menu
                  transition-duration="100"
                  @show="$tracker.openedPopup('unit_amenities', 'unit_details')"
                >
                  <div class="text-center text-bold q-pa-sm">
                    {{ props.row.prop.building_name || props.row.prop.street_address }} >
                    {{ props.row.unit.floorplan_name }} > {{ props.row.unit.unit_name }} > Amenities
                  </div>
                  <q-separator />
                  <q-list dense padding>
                    <template v-if="props.row.unit.amenities?.length">
                      <q-item-label header class="text-bold q-pb-xs">
                        Advertised Unit Amenities
                      </q-item-label>
                      <q-item-label class="text-grey-7 q-px-md q-pb-sm">
                        Captured from the leasing page of each property website.
                      </q-item-label>
                      <q-item-section class="q-px-sm items-start q-pb-sm">
                        <q-chip
                          v-for="(amenity, index) in props.row.unit.amenities"
                          :key="`amenity-${index}`"
                          dense
                          :color="$q.dark.isActive ? 'grey-8' : 'grey-5'"
                        >
                          {{ amenity }}
                        </q-chip>
                      </q-item-section>
                    </template>
                    <template v-if="props.row.unit.tags?.length">
                      <q-separator v-if="props.row.unit.amenities?.length" />
                      <q-item-label header class="text-bold q-pb-xs text-primary">
                        <q-icon name="auto_awesome"></q-icon>
                        AI-Detected Unit Amenities
                      </q-item-label>
                      <q-item-label class="text-grey-7 q-px-md q-pb-sm">
                        Detected from floorplan images, floorplan names, and advertised amenities.
                      </q-item-label>
                      <q-item-section class="q-px-sm items-start">
                        <q-chip
                          v-for="(tag, index) in filterTags(props.row.unit.tags)"
                          :key="`tag-${index}`"
                          :color="$q.dark.isActive ? 'grey-8' : 'grey-5'"
                          dense
                        >
                          {{ getAmenityTitle(tag) }}
                        </q-chip>
                      </q-item-section>
                    </template>
                  </q-list>
                </q-menu>
              </q-btn>
            </template>
            <chart-sparkline
              v-else-if="col.name === 'history' && props.row.unit.history?.length"
              :values="
                getTrend(
                  [props.row],
                  expandedUnitHistory,
                  dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
                  dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
                  'effective_rent'
                )
              "
              label="Effective Rent"
            />
            <template v-else>
              <q-icon
                v-if="
                  col.name === 'exit_market' &&
                  props.cols.find((_) => _.name === 'days_on_market').value &&
                  props.cols.find((_) => _.name === 'exit_market').value === 'Active'
                "
                name="info_outline"
              >
                <q-tooltip>
                  This listing is still on the market. It has been active for
                  <b>{{ props.cols.find((_) => _.name === 'days_on_market').value }} days</b>.
                </q-tooltip>
              </q-icon>
              <span v-if="col.name === 'ami_level' && col.value !== '--'">
                <q-icon name="o_info">
                  <q-tooltip max-width="300px">
                    <div v-for="line in getAMITooltipText(props)" :key="line">
                      {{ line }}
                    </div>
                  </q-tooltip>
                </q-icon>
                {{ col.value }}
              </span>
              <span v-else>{{ col.value }}</span>
              <q-btn
                v-if="
                  col.name === 'effective_price' &&
                  props.cols.find((_) => _.name === 'price').value !==
                    props.cols.find((_) => _.name === 'effective_price').value &&
                  props.row.unit.history?.length &&
                  col.value
                "
                color="primary"
                flat
                clickable
                dense
                size="sm"
                @click="openPriceHistory(props.row)"
              >
                <q-icon name="history" size="14px" />
              </q-btn>
            </template>
          </q-td>
        </q-tr>
      </template>
      <template #no-data>
        <slot name="no-data"></slot>
      </template>
    </q-table>
    <q-dialog v-model="displayUnitLevel" transition-duration="100">
      <unit-level-card
        v-if="selectedUnit"
        :selected-unit="selectedUnit"
        :expanded-unit-history="expandedUnitHistory"
        @close="displayUnitLevel = false"
      />
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { type PropType } from 'vue'
import { exportFile, type QTableColumn, type QTrProps } from 'quasar'
import { format, parseISO } from 'date-fns'

import {
  type AmiLevel,
  type Availability,
  availabilityDate,
  type AvailabilityPeriod,
  daysOnMarket,
  daysVacant,
  getAmenityTitle,
  getAMILimitRent,
  getEffPsf,
  getPsf,
  getTrend,
  getUnitAMILevel,
  type Price,
  type PropertyDetailsResponse,
  sanitizeFieldForCSV
} from 'common'

import { filterTags, formatMoney, formatNumber, unitTypeColor } from '@/utils'

import ChartSparkline from '@/components/Charts/ChartSparkline.vue'
import FilterPopover from '@/components/PropertyReports/FilterPopover.vue'
import type {
  ActiveUnitFilter,
  UnitFilterOption
} from '@/components/PropertyReports/unitDetailsFilters.types'
import UnitLevelCard from '@/components/PropertyReports/UnitLevelCard.vue'

type Row = {
  unit: Availability
  lastHistory: Price
  prop: PropertyDetailsResponse
  period: AvailabilityPeriod
}

export const exportCSV = (
  units: Row[],
  name: string,
  dateRange: { from: Date; to: Date },
  forceText = false
) => {
  const exportDecimal = (num: number | null) => (num ? Math.round(num * 100) / 100 : null)
  const today = new Date().toISOString().split('T')[0]
  const rows = units.map((row) => {
    const lastPrice = row.lastHistory
    return {
      'Property Name': sanitizeFieldForCSV(row.prop.building_name || row.prop.street_address),
      Bedrooms: row.unit.bed,
      Bathrooms: row.unit.bath,
      'Partial Bathrooms': row.unit.partial_bath,
      Floorplan: sanitizeFieldForCSV(row.unit.floorplan_name, { forceText }),
      Unit: sanitizeFieldForCSV(row.unit.unit_name, { forceText }),
      Floor: row.unit.floor,
      SF: row.unit.sqft || row.unit.min_sqft,
      Term: row.lastHistory.lease_term || row.unit.school_term,
      'Est AMI Level': (() => {
        const amiLevel = getUnitAMILevel(
          row.unit,
          row.prop,
          format(dateRange.from, 'yyyy-MM-dd'),
          format(dateRange.to, 'yyyy-MM-dd')
        )
        return amiLevel ? `${amiLevel}%` : '--'
      })(),
      'Last Asking Rent': lastPrice.price,
      'Asking Rent PSF': exportDecimal(
        getPsf({
          price: lastPrice.price,
          min_price: lastPrice.min_price,
          sqft: row.unit.sqft,
          min_sqft: row.unit.min_sqft,
          max_sqft: row.unit.max_sqft
        })
      ),
      'Last Effective Rent': lastPrice.effective_price,
      'Effective Rent PSF': exportDecimal(
        getEffPsf({
          effective_price: lastPrice.effective_price,
          min_effective_price: lastPrice.min_effective_price,
          sqft: row.unit.sqft,
          min_sqft: row.unit.min_sqft,
          max_sqft: row.unit.max_sqft
        })
      ),
      Deposit: row.period.deposit,
      'Available On': availabilityDate(row.period),
      'On Market Date': row.period.enter_market,
      'Off Market Date': row.period.exit_market,
      'Days on Market': daysOnMarket(row.period, new Date()),
      'Days Vacant': daysVacant(row.period, new Date())
    }
  })
  const header = [
    'Property Name',
    'Floorplan',
    'Unit',
    'Floor',
    'Bedrooms',
    'Bathrooms',
    'Partial Bathrooms',
    'SF',
    'Available On',
    'Deposit',
    'Term',
    'Est AMI Level',
    'Last Asking Rent',
    'Asking Rent PSF',
    'Last Effective Rent',
    'Effective Rent PSF',
    'On Market Date',
    'Off Market Date',
    'Days on Market',
    'Days Vacant'
  ]
  exportFile(
    `hello-data-${name}-${today}.csv`,
    header.join(',') +
      '\n' +
      rows
        .map((row) =>
          header
            .map((col) => {
              if (col in row) {
                return row[col]
              } else {
                console.warn(`Missing column ${col} in row`, row)
              }
            })
            .join(',')
        )
        .join('\n')
  )
}

export default {
  name: 'UnitDetailsTable',
  components: {
    ChartSparkline,
    FilterPopover,
    UnitLevelCard
  },
  emits: ['apply-column-filter', 'remove-column-filter'],
  props: {
    rows: {
      type: Array<Row>,
      default: null
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    bordered: {
      type: Boolean,
      default: true
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    dateRange: {
      type: Object as PropType<{
        from: Date
        to: Date
      }>,
      required: true
    },
    columnFilterOptions: {
      type: Array as PropType<UnitFilterOption[]>,
      default: () => []
    },
    columnFilterMap: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    },
    activeFilters: {
      type: Array as PropType<ActiveUnitFilter[]>,
      default: () => []
    }
  },
  data() {
    return {
      unitTypeColor,
      formatMoney,
      formatNumber,
      format,
      availabilityDate,
      parseISO,
      displayUnitLevel: false,
      selectedUnit: null as Row | null,
      pendingFilter: {
        filterLabel: null as string | null,
        values: [] as (string | number)[],
        minValue: null as null | number | Date,
        maxValue: null as null | number | Date
      },
      columns: [
        {
          field: (row: Row) => row.prop.building_name || row.prop.street_address,
          name: 'building_name',
          label: 'Property Name',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          classes: 'constrained-cell',
          description: 'The name of the property the unit belongs to.'
        },
        {
          field: (row: Row) => {
            const { bed, bath, partial_bath } = row.unit
            return `${bed}/${bath}${partial_bath ? '.5' : ''}`
          },
          name: 'bed',
          label: 'Type',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          description: 'Unit type as Bed / Bath'
        },
        {
          field: (row: Row) => row.unit.floorplan_name,
          name: 'floorplan_name',
          label: 'Floorplan',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          classes: 'constrained-cell',
          description: 'The name of the floorplan the unit belongs to, when available.'
        },
        {
          field: (row: Row) => row.unit.unit_name,
          name: 'unit_name',
          label: 'Unit',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          classes: 'constrained-cell',
          description: 'The name of the unit, when available.'
        },
        {
          field: (row: Row) => row.unit.floor,
          name: 'floor',
          label: 'Floor',
          sortable: true,
          align: 'left',
          sortOrder: 'da',
          description: 'Parsed floor number, using the unit name and floorplan name.'
        },
        {
          field: (row: Row) => {
            return row.unit.sqft || row.unit.min_sqft
          },
          name: 'sqft',
          label: 'SF',
          sortable: true,
          sortOrder: 'da',
          align: 'left',
          format: (_, row: Row) => {
            if (row.unit.sqft) {
              return formatNumber(row.unit.sqft)
            } else if (row.unit.min_sqft && row.unit.max_sqft) {
              return `${formatNumber(row.unit.min_sqft)} - ${formatNumber(row.unit.max_sqft)}`
            }
            return ''
          },
          description:
            'The square footage of the unit. When sqft is not available, we display the min-max range instead.'
        },
        {
          field: (row: Row) => {
            return row.lastHistory.lease_term || row.unit.school_term
          },
          name: 'term',
          label: 'Term',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          description:
            'When multiple lease terms have the same effective rent, the system selects the term closest to 12 months. In the event of a tie (e.g. 11 vs. 13 months), the shorter term is used. This logic prevents unrealistic concessions, such as 1 month free on a 3-month lease.',
          format: (val: number | null) => {
            if (typeof val === 'string') {
              return val
            }
            if (typeof val === 'number') {
              return `${val} mo.`
            }
            return '12 mo.*'
          }
        },
        {
          field: (row: Row) => {
            const amiLevel = getUnitAMILevel(
              row.unit,
              row.prop,
              this.dateRange?.from ? format(this.dateRange.from, 'yyyy-MM-dd') : '2023-06-17',
              this.dateRange?.to
                ? format(this.dateRange.to, 'yyyy-MM-dd')
                : format(new Date(), 'yyyy-MM-dd')
            )
            return amiLevel ? `${amiLevel}%` : '--'
          },
          name: 'ami_level',
          label: 'Est AMI Level',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          description:
            'The estimated highest Area Median Income (AMI) level that this unit could theoretically qualify for based on bedroom count and the 30% rule (rent should not exceed 30% of monthly income). This is a calculation to show potential affordability, not an official classification. Shows the percentage (30%, 50%, 60%, 80%, 120%) or blank if rent exceeds all AMI thresholds.'
        },
        {
          field: (row: Row) => {
            const lastPrice = row.lastHistory
            return lastPrice.price || lastPrice.min_price
          },
          name: 'price',
          label: 'Rent',
          sortable: true,
          sortOrder: 'da',
          align: 'left',
          format: (_, row: Row) => {
            const lastPrice = row.lastHistory
            if (lastPrice.price) {
              return formatMoney(lastPrice.price)
            } else if (lastPrice.min_price && lastPrice.max_price) {
              return `${formatMoney(lastPrice.min_price)} - ${formatMoney(lastPrice.max_price)}`
            }
            return ''
          },
          description:
            'The last asking rent is the most recent known asking rent for this unit. When the unit is marketed with a range, we display that instead.'
        },
        {
          field: (row: Row) => {
            return getPsf({
              price: row.lastHistory.price,
              min_price: row.lastHistory.min_price,
              sqft: row.unit.sqft,
              min_sqft: row.unit.min_sqft,
              max_sqft: row.unit.max_sqft
            })
          },
          name: 'rent_per_sqft',
          label: 'PSF',
          sortable: true,
          sortOrder: 'da',
          align: 'left',
          format: (price: number | null) => {
            return formatMoney(price, 2)
          },
          description:
            'The rent per sqft is the most recent known asking rent divided by the square footage of the unit.'
        },
        {
          field: (row: Row) => {
            const lastPrice = row.lastHistory
            return lastPrice.effective_price || lastPrice.min_effective_price
          },
          name: 'effective_price',
          label: 'NER',
          sortable: true,
          sortOrder: 'da',
          align: 'left',
          format: (_, row: Row) => {
            const lastPrice = row.lastHistory
            if (lastPrice.effective_price) {
              return formatMoney(lastPrice.effective_price)
            } else if (lastPrice.min_effective_price && lastPrice.max_effective_price) {
              return `${formatMoney(lastPrice.min_effective_price)} - ${formatMoney(
                lastPrice.max_effective_price
              )}`
            }
            return ''
          },
          description:
            'The net effective rent is the most recent known effective rent for this unit. Our AI extracts the dollar amounts from concession banners and amortizes them over the lease term. If only a floorplan is available, a range of effective rents is displayed instead.'
        },
        {
          field: (row: Row) => {
            return getEffPsf({
              effective_price: row.lastHistory.effective_price,
              min_effective_price: row.lastHistory.min_effective_price,
              sqft: row.unit.sqft,
              min_sqft: row.unit.min_sqft,
              max_sqft: row.unit.max_sqft
            })
          },
          name: 'effective_rent_per_sqft',
          label: 'PSF',
          sortable: true,
          sortOrder: 'da',
          align: 'left',
          format: (price: number | null) => {
            return formatMoney(price, 2)
          },
          description:
            'The effective rent per sqft is the most recent known effective rent divided by the square footage of the unit.'
        },
        {
          label: 'History',
          name: 'history',
          sortable: false,
          align: 'left',
          description:
            'The trend of the effective rent while the listing was active, or over the specified date range if a time range filter is active.'
        },
        {
          field: (row: Row) => {
            return row.period.availability
          },
          name: 'availability',
          label: 'Available On',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          format: (_, row: Row) => {
            return availabilityDate(row.period)
          },
          description: 'The date the unit becomes available for move-in.'
        },
        {
          field: (row: Row) => {
            return row.period.enter_market
          },
          name: 'enter_market',
          label: 'On Mkt Date',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          format: (val: string | null) => {
            return val ? format(parseISO(val), 'M/d/yyyy') : null
          },
          description:
            'The date this unit was first listed on the market. If we cannot find a date, we show 6/17/2023 instead.'
        },
        {
          field: (row: Row) => {
            return row.period.exit_market
          },
          name: 'exit_market',
          label: 'Leased Date',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          format: (val: string | null) => {
            return !val ? 'Active' : format(parseISO(val), 'M/d/yyyy')
          },
          description:
            'The date this unit was last removed from the market. If the unit is still on the market, we show Active instead.'
        },
        {
          field: (row: Row) => {
            return daysOnMarket(row.period, new Date())
          },
          name: 'days_on_market',
          label: 'Days on Mkt',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          description:
            'The number of days this unit has been on the market. If the unit is still on the market, we show the number of days since it was first listed.'
        },
        {
          field: (row: Row) => {
            return daysVacant(row.period, new Date()) || ''
          },
          name: 'days_vacant',
          label: 'Days Vacant',
          sortable: true,
          sortOrder: 'da',
          align: 'right',
          description:
            'The number of days this unit has been vacant. If the unit is still vacant, we show the number of days since it was last listed as available.'
        }
      ] as QTableColumn[]
    }
  },
  computed: {
    pendingSelectedFilter() {
      if (!this.pendingFilter.filterLabel) return null
      const filterOption = this.columnFilterOptions.find(
        (f) => f.label === this.pendingFilter.filterLabel
      )
      if (!filterOption) return null
      return {
        filter: filterOption,
        values: this.pendingFilter.values,
        minValue: this.pendingFilter.minValue,
        maxValue: this.pendingFilter.maxValue
      }
    }
  },
  watch: {
    displayUnitLevel(val: true) {
      if (val) {
        this.$tracker.openedPopup('unit_pricing_history')
      }
    }
  },
  methods: {
    filterTags,
    getAmenityTitle,
    getTrend,
    getId(row: Row) {
      return row.unit.id
    },
    openPriceHistory(row: Row) {
      this.selectedUnit = row
      this.displayUnitLevel = true
    },
    sortingChanged(colName: string) {
      this.$tracker.changedSort('unit_details', colName)
    },
    getAMITooltipText(props: QTrProps['props']): string[] {
      const amiLevelCol = props.cols.find((_: { name: string }) => _.name === 'ami_level')
      const amiLevelValue = amiLevelCol?.value
      if (!amiLevelValue || amiLevelValue === '--') {
        return []
      }

      const defaultTooltip = [`Qualifies for ${amiLevelValue} AMI`]

      // Extract the numeric AMI level (e.g., "80%" -> 80)
      const amiLevel = parseInt(amiLevelValue.replace('%', '')) as AmiLevel
      const rentCol = props.cols.find((_: { name: string }) => _.name === 'price')
      const rentValue = rentCol?.value
      if (!rentValue) {
        return defaultTooltip
      }

      // Parse the actual rent amount from the formatted string
      const rentMatch = rentValue.match(/\$?([\d,]+)/)
      const actualRent = rentMatch ? parseInt(rentMatch[1].replace(/,/g, '')) : null
      if (!actualRent) {
        return defaultTooltip
      }

      const amiLimitRent = getAMILimitRent(props.row.unit, props.row.prop, amiLevel)
      if (!amiLimitRent) {
        return defaultTooltip
      }

      const difference = Math.abs(actualRent - amiLimitRent)
      const percentOfAmi = Math.round((actualRent / amiLimitRent) * amiLevel)
      return [
        `${percentOfAmi}% of AMI `,
        `${formatMoney(difference)} difference from ${amiLevelValue} AMI threshold`,
        `${amiLevelValue} AMI Threshold: ${formatMoney(amiLimitRent)}`
      ]
    },
    isColumnFilterActive(colName: string): boolean {
      const filterLabel = this.columnFilterMap[colName]
      return this.activeFilters.some((_) => _.filter?.label === filterLabel)
    },
    initPendingFilter(colName: string) {
      const filterLabel = this.columnFilterMap[colName]
      if (!filterLabel) return
      const existing = this.activeFilters.find((_) => _.filter?.label === filterLabel)
      const option = this.columnFilterOptions.find((_) => _.label === filterLabel)
      this.pendingFilter = {
        filterLabel,
        values: existing?.values ? [...existing.values] : [],
        minValue: existing?.minValue ?? option?.minValue ?? null,
        maxValue: existing?.maxValue ?? option?.maxValue ?? null
      }
    },
    resetPendingFilter() {
      this.pendingFilter = { filterLabel: null, values: [], minValue: null, maxValue: null }
    },
    onMenuHide(colName: string) {
      if (this.pendingFilter.filterLabel === this.columnFilterMap[colName]) {
        this.resetPendingFilter()
      }
    },
    emitApplyFilter(colName: string) {
      const filterLabel = this.columnFilterMap[colName]
      if (!filterLabel) return
      this.$emit('apply-column-filter', {
        filterLabel,
        values: this.pendingFilter.values,
        minValue: this.pendingFilter.minValue,
        maxValue: this.pendingFilter.maxValue
      })
    },
    emitRemoveFilter(colName: string) {
      const filterLabel = this.columnFilterMap[colName]
      if (filterLabel) this.$emit('remove-column-filter', filterLabel)
      this.pendingFilter.values = []
      this.pendingFilter.minValue = null
      this.pendingFilter.maxValue = null
    }
  }
}
</script>

<style scoped lang="scss">
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
